import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MaterialModule } from '../../../material/material.module';
import { CurriculumAdminService } from '../services/curriculum-admin.service';
import { Book, BookManifest, Chapter, CurriculumManifest, Subject } from '../../../middle-school-math/models';
import { DataService } from '../../../middle-school-math/services/data';
import { AdminEditChapterDialogComponent } from './admin-edit-chapter-dialog.component';
import { AdminSubjectDialogComponent } from './admin-subject-dialog.component';
import { AdminBookDialogComponent } from './admin-book-dialog.component';
import { AdminCoursesConfigDialogComponent } from './admin-courses-config-dialog.component';

@Component({
  selector: 'app-admin-chapter-management',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, MaterialModule],
  template: `
    <div class="curriculum-page">
      <!-- Top Navigation & Header -->
      <div class="header-card">
        <div class="nav-breadcrumbs">
          <button mat-button routerLink="/admin/dashboard" class="back-btn">
            <mat-icon>arrow_back</mat-icon> Dashboard
          </button>
          <span class="crumb-separator">/</span>
          <span class="crumb-clickable" (click)="selectSubject(selectedSubject)">{{ selectedSubject?.name || 'Curriculum' }}</span>
          <span class="crumb-separator" *ngIf="selectedBook">/</span>
          <span class="current-crumb" *ngIf="selectedBook">{{ selectedBook?.title }}</span>
        </div>

        <div class="header-main">
          <div class="header-title-wrap">
            <div class="icon-avatar" [style.background]="selectedSubject?.color || '#1976d2'">
              <mat-icon>{{ selectedSubject?.icon || 'menu_book' }}</mat-icon>
            </div>
            <div>
              <h1 class="page-title">Curriculum & Content Management</h1>
              <p class="page-subtitle">Configure Subjects, Books, Lessons, Flashcards, and Quizzes</p>
            </div>
          </div>

          <div class="header-actions">
            <button mat-stroked-button (click)="openCoursesPageConfigDialog()">
              <mat-icon>tune</mat-icon> Configure Courses Page
            </button>
            <button mat-stroked-button color="primary" (click)="openRawCurriculumModal()">
              <mat-icon>data_object</mat-icon> Curriculum JSON
            </button>
            <button mat-stroked-button (click)="openAddSubjectDialog()">
              <mat-icon>add</mat-icon> Add Subject
            </button>
            <button mat-raised-button color="primary" (click)="openAddBookDialog()">
              <mat-icon>post_add</mat-icon> Add Book
            </button>
          </div>
        </div>

        <!-- LEVEL 1: SUBJECTS BAR -->
        <div class="subjects-bar-wrapper">
          <span class="tier-label">1. SUBJECTS:</span>
          <div class="subjects-pills">
            <button *ngFor="let subj of curriculum.subjects"
                    type="button"
                    class="subject-pill"
                    [class.active]="selectedSubject?.id === subj.id"
                    [style.--subj-color]="subj.color || '#1976d2'"
                    (click)="selectSubject(subj)">
              <mat-icon class="subj-icon">{{ subj.icon || 'category' }}</mat-icon>
              <span class="subj-name">{{ subj.name }}</span>
              <span class="subj-badge">{{ getBooksForSubject(subj.id).length }} books</span>
              <button mat-icon-button class="mini-edit-btn" (click)="$event.stopPropagation(); openEditSubjectDialog(subj)" matTooltip="Edit Subject">
                <mat-icon>settings</mat-icon>
              </button>
              <button mat-icon-button class="mini-edit-btn delete-pill-btn" (click)="$event.stopPropagation(); deleteSubject(subj)" matTooltip="Delete Subject">
                <mat-icon>delete_outline</mat-icon>
              </button>
            </button>
          </div>
        </div>

        <!-- LEVEL 2: BOOKS SHELF -->
        <div class="books-shelf-wrapper" *ngIf="selectedSubject">
          <div class="shelf-header">
            <span class="tier-label">2. BOOKS IN {{ selectedSubject.name | uppercase }}:</span>
            <button mat-button color="primary" class="mini-add-btn" (click)="openAddBookDialog()">
              <mat-icon>add</mat-icon> New Book in {{ selectedSubject.name }}
            </button>
          </div>

          <div *ngIf="currentSubjectBooks.length === 0" class="no-books-box">
            <p>No books created yet under {{ selectedSubject.name }}. Click "New Book" to create one!</p>
          </div>

          <div class="books-cards-row" *ngIf="currentSubjectBooks.length > 0">
            <div *ngFor="let book of currentSubjectBooks"
                 class="book-card"
                 [class.active]="selectedBook?.id === book.id"
                 (click)="selectBook(book)">
              <div class="book-card-header">
                <div class="book-icon-badge">
                  <mat-icon>{{ book.icon || 'auto_stories' }}</mat-icon>
                </div>
                <div class="book-menu-actions">
                  <button mat-icon-button (click)="$event.stopPropagation(); openEditBookDialog(book)" matTooltip="Edit Book Details">
                    <mat-icon>edit</mat-icon>
                  </button>
                  <button mat-icon-button color="warn" (click)="$event.stopPropagation(); deleteBook(book)" matTooltip="Delete Book">
                    <mat-icon>delete_outline</mat-icon>
                  </button>
                </div>
              </div>
              <div class="book-card-body">
                <h3 class="book-title">{{ book.title }}</h3>
                <p class="book-desc">{{ book.description || 'No description provided' }}</p>
                <div class="book-footer-meta">
                  <span class="chapters-count-tag">
                    <mat-icon>menu_book</mat-icon>
                    {{ selectedBook?.id === book.id ? manifest.chapters.length : 'Manage Chapters' }}
                  </span>
                  <span class="sub-pricing-tag" [class.enabled]="book.isSubscriptionEnabled">
                    {{ book.isSubscriptionEnabled ? ('⚡ $' + (book.monthlyPrice || 9.99) + '/mo · $' + (book.annualPrice || 79.99) + '/yr') : '🆓 Free' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- LEVEL 3: CHAPTERS SECTION FOR ACTIVE BOOK -->
      <div *ngIf="selectedBook" class="chapters-container">
        <div class="chapters-ctrl-bar">
          <div class="ctrl-title">
            <mat-icon class="book-hero-icon">menu_book</mat-icon>
            <div>
              <div class="ctrl-title-row">
                <h2>Chapters in {{ selectedBook.title }}</h2>
                <span class="book-sub-status-pill" [class.sub-active]="selectedBook.isSubscriptionEnabled">
                  <mat-icon>{{ selectedBook.isSubscriptionEnabled ? 'workspace_premium' : 'lock_open' }}</mat-icon>
                  {{ selectedBook.isSubscriptionEnabled ? ('Paid Subscription: $' + (selectedBook.monthlyPrice || 9.99) + '/mo | $' + (selectedBook.annualPrice || 79.99) + '/yr') : 'Open Access (Free)' }}
                </span>
              </div>
              <p class="ctrl-subtitle">File: <code>{{ selectedBook.chaptersFile || selectedBook.id + '_chapters.json' }}</code> • {{ manifest.chapters.length }} Chapters total</p>
            </div>
          </div>

          <div class="ctrl-actions">
            <mat-form-field appearance="outline" class="search-field">
              <mat-label>Search chapters...</mat-label>
              <mat-icon matPrefix>search</mat-icon>
              <input matInput [(ngModel)]="searchQuery" (ngModelChange)="filterChapters()" placeholder="e.g. Algebra, Forces, etc." />
              <button mat-icon-button matSuffix *ngIf="searchQuery" (click)="searchQuery = ''; filterChapters()">
                <mat-icon>clear</mat-icon>
              </button>
            </mat-form-field>
            <button mat-stroked-button (click)="openRawChaptersModal()">
              <mat-icon>code</mat-icon> Raw JSON
            </button>
            <button mat-raised-button color="primary" (click)="openAddChapterDialog()">
              <mat-icon>add_circle</mat-icon> Add Chapter
            </button>
          </div>
        </div>

        <!-- Loading / Error States -->
        <div *ngIf="loadingChapters" class="loading-state">
          <mat-spinner diameter="40"></mat-spinner>
          <p>Loading chapters for {{ selectedBook.title }}...</p>
        </div>

        <div *ngIf="errorMsg && !loadingChapters" class="error-banner">
          <mat-icon>error_outline</mat-icon>
          <div class="error-details">{{ errorMsg }}</div>
          <button mat-stroked-button color="warn" (click)="loadChaptersForBook(selectedBook)">Retry</button>
        </div>

        <!-- Chapters Grid -->
        <div *ngIf="!loadingChapters && !errorMsg" class="chapters-body">
          <div *ngIf="filteredChapters.length === 0" class="empty-chapters-box">
            <mat-icon>menu_book</mat-icon>
            <h3>No chapters in this book yet</h3>
            <p>Click "Add Chapter" to create the first chapter with lesson pages, flashcards, and quizzes.</p>
            <button mat-raised-button color="primary" (click)="openAddChapterDialog()">
              <mat-icon>add</mat-icon> Add Chapter
            </button>
          </div>

          <div class="chapters-grid" *ngIf="filteredChapters.length > 0">
            <div *ngFor="let chapter of filteredChapters; let idx = index" class="chapter-card">
              <!-- Top Bar -->
              <div class="card-top-bar">
                <div class="top-badge-group">
                  <div class="chapter-number-badge">#{{ getRealIndex(chapter) + 1 }}</div>
                  <span class="preview-badge free" *ngIf="chapter.isFreePreview">
                    <mat-icon class="badge-mini-icon">visibility</mat-icon> Free Preview
                  </span>
                  <span class="preview-badge sub-only" *ngIf="!chapter.isFreePreview && selectedBook.isSubscriptionEnabled">
                    <mat-icon class="badge-mini-icon">lock</mat-icon> Subscriber Only
                  </span>
                </div>
                <div class="reorder-actions">
                  <button mat-icon-button (click)="moveChapter(chapter, -1)" [disabled]="getRealIndex(chapter) === 0" matTooltip="Move Up">
                    <mat-icon>keyboard_arrow_up</mat-icon>
                  </button>
                  <button mat-icon-button (click)="moveChapter(chapter, 1)" [disabled]="getRealIndex(chapter) === manifest.chapters.length - 1" matTooltip="Move Down">
                    <mat-icon>keyboard_arrow_down</mat-icon>
                  </button>
                </div>
              </div>

              <!-- Content -->
              <div class="card-body">
                <h3 class="chapter-title">{{ chapter.title }}</h3>
                <div class="id-slug-row">
                  <span class="slug-tag"><code>{{ chapter.id }}</code></span>
                  <span class="prefix-tag" *ngIf="chapter.prefix">Prefix: <code>{{ chapter.prefix }}</code></span>
                </div>

                <!-- Metrics Grid -->
                <div class="metrics-grid">
                  <div class="metric-item">
                    <mat-icon class="metric-icon slides-color">slideshow</mat-icon>
                    <div class="metric-info">
                      <span class="metric-val">{{ chapter.slides?.length || 0 }}</span>
                      <span class="metric-lbl">Pages</span>
                    </div>
                  </div>

                  <div class="metric-item">
                    <mat-icon class="metric-icon cards-color">style</mat-icon>
                    <div class="metric-info">
                      <span class="metric-val">{{ chapter.flashCards?.length || 0 }}</span>
                      <span class="metric-lbl">Cards</span>
                    </div>
                  </div>

                  <div class="metric-item">
                    <mat-icon class="metric-icon quiz-color">quiz</mat-icon>
                    <div class="metric-info">
                      <span class="metric-val">{{ chapter.quizQuestions?.length || 0 }}</span>
                      <span class="metric-lbl">Quizzes</span>
                    </div>
                  </div>

                  <div class="metric-item">
                    <mat-icon class="metric-icon pass-color">verified</mat-icon>
                    <div class="metric-info">
                      <span class="metric-val">{{ chapter.quizPassScorePercentage || '80' }}%</span>
                      <span class="metric-lbl">Pass Score</span>
                    </div>
                  </div>
                </div>
              </div>

              <mat-divider></mat-divider>

              <!-- Card Actions -->
              <div class="card-footer">
                <a mat-button [routerLink]="['/middle-school-math/chapter', chapter.id]" target="_blank" class="test-btn" matTooltip="Preview lesson in player">
                  <mat-icon>open_in_new</mat-icon> Test
                </a>
                <div class="footer-primary-actions">
                  <button mat-stroked-button color="primary" (click)="openEditChapterDialog(chapter)">
                    <mat-icon>edit</mat-icon> Configure
                  </button>
                  <button mat-icon-button color="warn" (click)="deleteChapter(chapter)" matTooltip="Delete chapter">
                    <mat-icon>delete_outline</mat-icon>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .curriculum-page {
      padding: 1.5rem 2rem;
      background: #f8fafc;
      min-height: calc(100vh - 64px);
    }
    .header-card {
      background: white;
      border-radius: 12px;
      padding: 1.5rem 2rem;
      box-shadow: 0 2px 8px rgba(0,0,0,0.06);
      margin-bottom: 2rem;
    }
    .nav-breadcrumbs {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 1rem;
      font-size: 0.9rem;
      color: #64748b;
    }
    .back-btn {
      color: #1976d2;
      font-weight: 500;
      padding: 0 0.5rem;
    }
    .crumb-separator { color: #cbd5e1; }
    .crumb-clickable {
      color: #1976d2;
      cursor: pointer;
      font-weight: 600;
    }
    .crumb-clickable:hover { text-decoration: underline; }
    .current-crumb { font-weight: 700; color: #1e293b; }

    .header-main {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 1rem;
      margin-bottom: 1.5rem;
    }
    .header-title-wrap {
      display: flex;
      align-items: center;
      gap: 1.25rem;
    }
    .icon-avatar {
      color: white;
      height: 56px;
      width: 56px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 10px rgba(0,0,0,0.15);
      transition: background 0.3s;
    }
    .icon-avatar mat-icon { font-size: 32px; height: 32px; width: 32px; }
    .page-title {
      margin: 0;
      font-size: 1.6rem;
      font-weight: 700;
      color: #1e293b;
    }
    .page-subtitle {
      margin: 0.25rem 0 0 0;
      color: #64748b;
      font-size: 0.95rem;
    }
    .header-actions {
      display: flex;
      gap: 0.75rem;
      align-items: center;
      flex-wrap: wrap;
    }

    /* LEVEL 1: SUBJECTS BAR */
    .subjects-bar-wrapper {
      padding: 1rem 0;
      border-top: 1px solid #f1f5f9;
      border-bottom: 1px solid #f1f5f9;
      display: flex;
      align-items: center;
      gap: 1rem;
      flex-wrap: wrap;
    }
    .tier-label {
      font-size: 0.75rem;
      font-weight: 800;
      letter-spacing: 0.05em;
      color: #94a3b8;
    }
    .subjects-pills {
      display: flex;
      gap: 0.75rem;
      flex-wrap: wrap;
    }
    .subject-pill {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      background: #f8fafc;
      border: 2px solid #e2e8f0;
      border-radius: 30px;
      cursor: pointer;
      transition: all 0.2s;
      font-size: 0.95rem;
      font-weight: 600;
      color: #475569;
    }
    .subject-pill:hover {
      border-color: var(--subj-color);
      background: white;
    }
    .subject-pill.active {
      background: var(--subj-color);
      color: white;
      border-color: var(--subj-color);
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    }
    .subj-icon { font-size: 20px; height: 20px; width: 20px; }
    .subj-badge {
      font-size: 0.75rem;
      padding: 0.15rem 0.5rem;
      background: rgba(0,0,0,0.08);
      border-radius: 12px;
    }
    .subject-pill.active .subj-badge {
      background: rgba(255,255,255,0.25);
      color: white;
    }
    .mini-edit-btn {
      height: 24px;
      width: 24px;
      line-height: 24px;
      margin-left: 0.15rem;
      opacity: 0.8;
      transition: all 0.2s;
    }
    .mini-edit-btn:hover { opacity: 1; }
    .mini-edit-btn mat-icon { font-size: 16px; height: 16px; width: 16px; }
    .delete-pill-btn:hover { color: #ef4444 !important; }

    /* LEVEL 2: BOOKS SHELF */
    .books-shelf-wrapper {
      padding-top: 1.25rem;
    }
    .shelf-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;
    }
    .mini-add-btn { font-size: 0.85rem; }
    .no-books-box {
      padding: 1.5rem;
      text-align: center;
      background: #f8fafc;
      border: 1px dashed #cbd5e1;
      border-radius: 8px;
      color: #64748b;
    }
    .books-cards-row {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 1rem;
    }
    .book-card {
      background: #f8fafc;
      border: 2px solid #e2e8f0;
      border-radius: 10px;
      padding: 1rem 1.25rem;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
    .book-card:hover {
      border-color: #93c5fd;
      background: white;
      transform: translateY(-2px);
    }
    .book-card.active {
      background: white;
      border-color: #1976d2;
      box-shadow: 0 4px 16px rgba(25,118,210,0.15);
    }
    .book-card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
    }
    .book-icon-badge {
      background: #eff6ff;
      color: #1d4ed8;
      height: 38px;
      width: 38px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .book-card.active .book-icon-badge {
      background: #1976d2;
      color: white;
    }
    .book-title {
      margin: 0 0 0.25rem 0;
      font-size: 1.1rem;
      font-weight: 700;
      color: #1e293b;
    }
    .book-desc {
      font-size: 0.82rem;
      color: #64748b;
      margin: 0 0 0.75rem 0;
      line-height: 1.35;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .book-footer-meta {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .chapters-count-tag {
      display: flex;
      align-items: center;
      gap: 0.35rem;
      font-size: 0.78rem;
      font-weight: 600;
      color: #1d4ed8;
      background: #eff6ff;
      padding: 0.2rem 0.6rem;
      border-radius: 12px;
    }
    .chapters-count-tag mat-icon { font-size: 16px; height: 16px; width: 16px; }

    /* LEVEL 3: CHAPTERS CONTAINER */
    .chapters-container {
      background: white;
      border-radius: 12px;
      padding: 1.5rem 2rem;
      box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    }
    .chapters-ctrl-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 1rem;
      margin-bottom: 1.5rem;
      padding-bottom: 1rem;
      border-bottom: 1px solid #e2e8f0;
    }
    .ctrl-title {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
    .book-hero-icon {
      font-size: 36px;
      height: 36px;
      width: 36px;
      color: #1976d2;
    }
    .ctrl-title h2 {
      margin: 0;
      font-size: 1.35rem;
      font-weight: 700;
      color: #1e293b;
    }
    .ctrl-subtitle {
      margin: 0.2rem 0 0 0;
      font-size: 0.85rem;
      color: #64748b;
    }
    .ctrl-actions {
      display: flex;
      gap: 0.75rem;
      align-items: center;
      flex-wrap: wrap;
    }
    .search-field {
      width: 250px;
      margin-bottom: -1.25em;
    }

    .loading-state, .empty-chapters-box {
      text-align: center;
      padding: 3rem 2rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1rem;
      color: #64748b;
    }
    .empty-chapters-box mat-icon { font-size: 48px; height: 48px; width: 48px; opacity: 0.5; }
    .error-banner {
      display: flex;
      align-items: center;
      gap: 1rem;
      background: #fee2e2;
      border: 1px solid #fca5a5;
      padding: 1rem 1.5rem;
      border-radius: 8px;
      color: #991b1b;
      margin-bottom: 1.5rem;
    }
    .error-details { flex: 1; }

    /* Chapters Grid */
    .chapters-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
      gap: 1.5rem;
    }
    .chapter-card {
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      transition: transform 0.2s, box-shadow 0.2s;
    }
    .chapter-card:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 20px rgba(0,0,0,0.08);
      border-color: #cbd5e1;
    }
    .card-top-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 1rem 0 1rem;
    }
    .chapter-number-badge {
      background: #eff6ff;
      color: #1d4ed8;
      font-weight: 700;
      font-size: 0.85rem;
      padding: 0.25rem 0.65rem;
      border-radius: 6px;
      border: 1px solid #bfdbfe;
    }
    .reorder-actions { display: flex; }
    .reorder-actions button { height: 32px; width: 32px; line-height: 32px; }

    .card-body {
      padding: 0.75rem 1.25rem 1.25rem 1.25rem;
      flex: 1;
    }
    .chapter-title {
      margin: 0 0 0.5rem 0;
      font-size: 1.15rem;
      font-weight: 700;
      color: #1e293b;
      line-height: 1.35;
    }
    .id-slug-row {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin-bottom: 1.25rem;
    }
    .slug-tag, .prefix-tag {
      background: #f1f5f9;
      padding: 0.2rem 0.5rem;
      border-radius: 4px;
      font-size: 0.75rem;
      color: #475569;
    }
    .metrics-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 0.75rem;
    }
    .metric-item {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      background: #f8fafc;
      border: 1px solid #f1f5f9;
      padding: 0.5rem 0.75rem;
      border-radius: 8px;
    }
    .metric-icon { font-size: 22px; height: 22px; width: 22px; }
    .slides-color { color: #2563eb; }
    .cards-color { color: #0d9488; }
    .quiz-color { color: #ea580c; }
    .pass-color { color: #16a34a; }
    .metric-info { display: flex; flex-direction: column; }
    .metric-val { font-weight: 700; font-size: 0.95rem; color: #1e293b; line-height: 1.1; }
    .metric-lbl { font-size: 0.72rem; color: #64748b; }

    .sub-pricing-tag {
      font-size: 0.75rem;
      font-weight: 700;
      color: #64748b;
      background: #f1f5f9;
      padding: 0.2rem 0.5rem;
      border-radius: 12px;
    }
    .sub-pricing-tag.enabled {
      color: #7c3aed;
      background: #f5f3ff;
      border: 1px solid #ddd6fe;
    }

    .ctrl-title-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      flex-wrap: wrap;
    }
    .book-sub-status-pill {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      font-size: 0.8rem;
      font-weight: 600;
      padding: 0.25rem 0.65rem;
      border-radius: 20px;
      background: #f1f5f9;
      color: #475569;
    }
    .book-sub-status-pill.sub-active {
      background: #fdf4ff;
      color: #9333ea;
      border: 1px solid #f0abfc;
    }
    .book-sub-status-pill mat-icon {
      font-size: 16px;
      height: 16px;
      width: 16px;
    }

    .top-badge-group {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .preview-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      font-size: 0.75rem;
      font-weight: 700;
      padding: 0.2rem 0.6rem;
      border-radius: 6px;
    }
    .preview-badge.free {
      background: #ecfdf5;
      color: #059669;
      border: 1px solid #a7f3d0;
    }
    .preview-badge.sub-only {
      background: #fef3c7;
      color: #d97706;
      border: 1px solid #fde68a;
    }
    .badge-mini-icon {
      font-size: 14px;
      height: 14px;
      width: 14px;
    }

    .card-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 1rem;
      background: #fafafa;
    }
    .footer-primary-actions { display: flex; gap: 0.5rem; align-items: center; }
    .test-btn { color: #64748b; font-size: 0.85rem; }
  `]
})
export class AdminChapterManagementComponent implements OnInit {
  curriculum: CurriculumManifest = { subjects: [], books: [] };
  selectedSubject: Subject | null = null;
  selectedBook: Book | null = null;

  manifest: BookManifest = { chapters: [] };
  filteredChapters: Chapter[] = [];
  searchQuery = '';
  loadingChapters = false;
  errorMsg: string | null = null;

  constructor(
    private curriculumAdminService: CurriculumAdminService,
    private dataService: DataService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.loadCurriculum();
  }

  loadCurriculum(preserveSelection = true) {
    this.dataService.refreshCurriculum().subscribe({ error: () => {} });
    this.curriculumAdminService.getCurriculum().subscribe({
      next: (data) => {
        this.curriculum = data;
        if (preserveSelection && this.selectedSubject) {
          const matchedSubj = this.curriculum.subjects.find(s => s.id === this.selectedSubject?.id);
          this.selectedSubject = matchedSubj || (this.curriculum.subjects[0] || null);
        } else {
          this.selectedSubject = this.curriculum.subjects[0] || null;
        }

        if (preserveSelection && this.selectedBook) {
          const matchedBook = this.curriculum.books.find(b => b.id === this.selectedBook?.id);
          this.selectedBook = matchedBook || (this.currentSubjectBooks[0] || null);
        } else {
          this.selectedBook = this.currentSubjectBooks[0] || null;
        }

        if (this.selectedBook) {
          this.loadChaptersForBook(this.selectedBook);
        }
      },
      error: (err) => {
        console.error('Failed to load curriculum:', err);
      }
    });
  }

  get currentSubjectBooks(): Book[] {
    if (!this.selectedSubject) return [];
    return this.curriculum.books.filter(b => b.subjectId === this.selectedSubject?.id);
  }

  getBooksForSubject(subjectId: string): Book[] {
    return this.curriculum.books.filter(b => b.subjectId === subjectId);
  }

  selectSubject(subj: Subject | null) {
    this.selectedSubject = subj;
    const books = this.currentSubjectBooks;
    if (books.length > 0) {
      this.selectBook(books[0]);
    } else {
      this.selectedBook = null;
      this.manifest = { chapters: [] };
      this.filteredChapters = [];
    }
  }

  selectBook(book: Book) {
    this.selectedBook = book;
    this.loadChaptersForBook(book);
  }

  loadChaptersForBook(book: Book) {
    this.loadingChapters = true;
    this.errorMsg = null;
    this.curriculumAdminService.getBookChapters(book.id).subscribe({
      next: (data) => {
        this.manifest = data && data.chapters ? data : { chapters: [] };
        this.filterChapters();
        this.loadingChapters = false;
      },
      error: (err) => {
        console.error('Failed to load chapters for book:', err);
        this.errorMsg = err.error?.message || err.message || 'Unable to connect to backend';
        this.loadingChapters = false;
      }
    });
  }

  filterChapters() {
    if (!this.searchQuery || this.searchQuery.trim() === '') {
      this.filteredChapters = [...this.manifest.chapters];
    } else {
      const q = this.searchQuery.toLowerCase().trim();
      this.filteredChapters = this.manifest.chapters.filter(c =>
        (c.title && c.title.toLowerCase().includes(q)) ||
        (c.id && c.id.toLowerCase().includes(q)) ||
        (c.prefix && c.prefix.toLowerCase().includes(q))
      );
    }
  }

  getRealIndex(chapter: Chapter): number {
    return this.manifest.chapters.findIndex(c => c.id === chapter.id);
  }

  moveChapter(chapter: Chapter, direction: number) {
    if (!this.selectedBook) return;
    const currentIndex = this.getRealIndex(chapter);
    const targetIndex = currentIndex + direction;

    if (targetIndex >= 0 && targetIndex < this.manifest.chapters.length) {
      const item = this.manifest.chapters.splice(currentIndex, 1)[0];
      this.manifest.chapters.splice(targetIndex, 0, item);

      this.curriculumAdminService.saveBookChapters(this.selectedBook.id, this.manifest).subscribe({
        next: () => this.filterChapters(),
        error: (err) => console.error('Failed to save chapter order:', err)
      });
    }
  }

  // --- Subject Actions ---
  openAddSubjectDialog() {
    const dialogRef = this.dialog.open(AdminSubjectDialogComponent, {
      width: '580px',
      data: { isNew: true }
    });

    dialogRef.afterClosed().subscribe((newSubj: Subject) => {
      if (newSubj) {
        this.curriculumAdminService.addSubject(newSubj).subscribe({
          next: () => {
            this.loadCurriculum();
          },
          error: (err) => alert('Failed to add subject: ' + (err.error?.message || err.message))
        });
      }
    });
  }

  openEditSubjectDialog(subj: Subject) {
    const dialogRef = this.dialog.open(AdminSubjectDialogComponent, {
      width: '580px',
      data: { subject: subj, isNew: false }
    });

    dialogRef.afterClosed().subscribe((res: any) => {
      if (!res) return;
      if (res.delete) {
        this.deleteSubject(subj);
      } else {
        this.curriculumAdminService.updateSubject(subj.id, res).subscribe({
          next: () => {
            this.loadCurriculum();
          },
          error: (err) => alert('Failed to update subject: ' + (err.error?.message || err.message))
        });
      }
    });
  }

  deleteSubject(subj: Subject) {
    const bookCount = this.getBooksForSubject(subj.id).length;
    const msg = `Are you sure you want to delete Subject "${subj.name}" (${subj.id})?\n\n` +
      (bookCount > 0
        ? `WARNING: This will permanently delete this subject, all ${bookCount} book(s) inside it, and all chapters within those books!`
        : `This will remove the subject category.`);
    if (confirm(msg)) {
      this.curriculumAdminService.deleteSubject(subj.id).subscribe({
        next: () => {
          if (this.selectedSubject?.id === subj.id) {
            this.selectedSubject = null;
          }
          this.loadCurriculum(false);
        },
        error: (err) => alert('Failed to delete subject: ' + (err.error?.message || err.message))
      });
    }
  }

  openCoursesPageConfigDialog() {
    this.dialog.open(AdminCoursesConfigDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      data: {}
    });
  }

  // --- Book Actions ---
  openAddBookDialog() {
    const dialogRef = this.dialog.open(AdminBookDialogComponent, {
      width: '580px',
      data: {
        subjects: this.curriculum.subjects,
        defaultSubjectId: this.selectedSubject?.id,
        isNew: true
      }
    });

    dialogRef.afterClosed().subscribe((newBook: Book) => {
      if (newBook) {
        this.curriculumAdminService.addBook(newBook).subscribe({
          next: () => {
            this.loadCurriculum();
          },
          error: (err) => alert('Failed to add book: ' + (err.error?.message || err.message))
        });
      }
    });
  }

  openEditBookDialog(book: Book) {
    const dialogRef = this.dialog.open(AdminBookDialogComponent, {
      width: '580px',
      data: {
        book,
        subjects: this.curriculum.subjects,
        isNew: false
      }
    });

    dialogRef.afterClosed().subscribe((updatedBook: Book) => {
      if (updatedBook) {
        this.curriculumAdminService.updateBook(book.id, updatedBook).subscribe({
          next: () => {
            this.loadCurriculum();
          },
          error: (err) => alert('Failed to update book: ' + (err.error?.message || err.message))
        });
      }
    });
  }

  deleteBook(book: Book) {
    if (confirm(`Are you sure you want to delete book "${book.title}" (${book.id})?\n\nThis will remove the book and its chapters.`)) {
      this.curriculumAdminService.deleteBook(book.id).subscribe({
        next: () => {
          this.loadCurriculum();
        },
        error: (err) => alert('Failed to delete book: ' + (err.error?.message || err.message))
      });
    }
  }

  // --- Chapter Actions ---
  openAddChapterDialog() {
    if (!this.selectedBook) return;
    const dialogRef = this.dialog.open(AdminEditChapterDialogComponent, {
      width: '850px',
      maxWidth: '95vw',
      data: { isNew: true }
    });

    dialogRef.afterClosed().subscribe((newChapter: Chapter) => {
      if (newChapter && this.selectedBook) {
        this.curriculumAdminService.addChapterToBook(this.selectedBook.id, newChapter).subscribe({
          next: () => {
            this.loadChaptersForBook(this.selectedBook!);
          },
          error: (err) => alert('Failed to add chapter: ' + (err.error?.message || err.message))
        });
      }
    });
  }

  openEditChapterDialog(chapter: Chapter) {
    if (!this.selectedBook) return;
    const dialogRef = this.dialog.open(AdminEditChapterDialogComponent, {
      width: '850px',
      maxWidth: '95vw',
      data: { chapter, isNew: false }
    });

    dialogRef.afterClosed().subscribe((updatedChapter: Chapter) => {
      if (updatedChapter && this.selectedBook) {
        this.curriculumAdminService.updateChapterInBook(this.selectedBook.id, chapter.id, updatedChapter).subscribe({
          next: () => {
            this.loadChaptersForBook(this.selectedBook!);
          },
          error: (err) => alert('Failed to update chapter: ' + (err.error?.message || err.message))
        });
      }
    });
  }

  deleteChapter(chapter: Chapter) {
    if (!this.selectedBook) return;
    if (confirm(`Are you sure you want to delete chapter "${chapter.title}" from ${this.selectedBook.title}?`)) {
      this.curriculumAdminService.deleteChapterFromBook(this.selectedBook.id, chapter.id).subscribe({
        next: () => {
          this.loadChaptersForBook(this.selectedBook!);
        },
        error: (err) => alert('Failed to delete chapter: ' + (err.error?.message || err.message))
      });
    }
  }

  openRawCurriculumModal() {
    const jsonStr = JSON.stringify(this.curriculum, null, 2);
    const w = window.open('', '_blank', 'width=800,height=700');
    if (w) {
      w.document.write(`
        <html><head><title>Curriculum Manifest</title><style>body { font-family: monospace; background: #1e1e1e; color: #4ec9b0; padding: 1.5rem; }</style></head>
        <body><pre>${jsonStr.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre></body></html>
      `);
      w.document.close();
    }
  }

  openRawChaptersModal() {
    const jsonStr = JSON.stringify(this.manifest, null, 2);
    const w = window.open('', '_blank', 'width=800,height=700');
    if (w) {
      w.document.write(`
        <html><head><title>${this.selectedBook?.title} Chapters</title><style>body { font-family: monospace; background: #1e1e1e; color: #4ec9b0; padding: 1.5rem; }</style></head>
        <body><pre>${jsonStr.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre></body></html>
      `);
      w.document.close();
    }
  }
}
